import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { articles, seoArticle } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.sbs';

function generateArticleLd(article: typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imgSrc,
    url: `${siteUrl}/guide/${article.slug}`,
    datePublished: '2026-01-15',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'Yoybuy Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yoybuy Spreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    inLanguage: 'ru',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/guide/${article.slug}`,
    },
  };
}

function renderContent(content: typeof seoArticle.content) {
  return content.map((block, idx) => {
    if (block.type === 'heading') {
      return (
        <h2 key={idx} className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-12 mb-5">
          {block.text}
        </h2>
      );
    }
    return (
      <p key={idx} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
        <span dangerouslySetInnerHTML={{ __html: block.text }} />
      </p>
    );
  });
}

function getArticleBySlug(slug: string) {
  return articles.find((a) => a.slug === slug);
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  const articleLd = useMemo(() => {
    if (!article) return null;
    return generateArticleLd(article);
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center px-6">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Статья не найдена</h1>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 bg-foreground-950 text-white text-sm font-medium rounded-lg hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
              <span>← На главную</span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50">
        <article className="px-6 md:px-10 max-w-[1100px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <aside className="lg:w-3/12 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-foreground-950 text-white text-sm font-medium rounded-lg hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap mb-8"
                >
                  <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  <span>← На главную Yoybuy.sbs</span>
                </Link>

                <div className="hidden lg:block">
                  <div className="w-full h-px bg-background-300/60 mb-6"></div>
                  <p className="text-xs text-foreground-500 uppercase tracking-wider mb-4 whitespace-nowrap">Другие статьи</p>
                  <nav className="space-y-2">
                    {articles.filter(a => a.slug !== slug).slice(0, 8).map((a) => (
                      <Link
                        key={a.id}
                        to={`/guide/${a.slug}`}
                        className="block text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap truncate"
                      >
                        {a.title.length > 50 ? a.title.slice(0, 50) + '...' : a.title}
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>

            <div className="lg:w-9/12 min-w-0">
              <nav className="text-xs text-foreground-400 mb-6 whitespace-nowrap flex flex-wrap gap-1">
                <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">Главная</Link>
                <span className="mx-1">/</span>
                <Link to="/guide/yoybuy-sheet-guide" className="hover:text-foreground-700 transition-colors cursor-pointer">Руководство</Link>
                <span className="mx-1">/</span>
                <span className="text-foreground-600">{article.title.length > 60 ? article.title.slice(0, 60) + '...' : article.title}</span>
              </nav>

              <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.imgSrc.replace(/width=\d+/, 'width=900').replace(/height=\d+/, 'height=385')}
                  alt={article.title}
                  title={`${article.title} — yoybuy spreadsheet`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-10">
                {article.title}
              </h1>

              <div className="prose-custom max-w-none">
                {renderContent(seoArticle.content)}
              </div>

              <div className="mt-12 mb-8">
                <p className="text-xs text-foreground-500 uppercase tracking-wider mb-4 whitespace-nowrap">Читайте также</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {articles.filter(a => a.slug !== slug).slice(0, 4).map((a) => (
                    <Link
                      key={a.id}
                      to={`/guide/${a.slug}`}
                      className="flex items-center gap-3 p-4 bg-background-100 rounded-xl hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={a.imgSrc} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className="text-sm text-foreground-800 font-medium line-clamp-2">{a.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-6 md:p-8 bg-background-100 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <p className="text-lg md:text-xl font-heading font-semibold text-foreground-950 whitespace-nowrap">
                  Готовы начать покупать?
                </p>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group inline-flex items-center gap-0 px-1 py-1 pl-6 bg-foreground-950 text-white rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center -ml-3">
                    <i className="ri-shopping-bag-3-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="ml-3 text-sm font-semibold uppercase tracking-wide whitespace-nowrap">YOYBUY SHEET</span>
                  <i className="ri-arrow-right-up-line text-white ml-3 mr-4 text-base w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
        {articleLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
          />
        )}
      </main>
      <Footer />
    </>
  );
}